'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Dayjs } from 'dayjs';
import type { CalendarGrid } from '@peach/types/schedule/types.js';
import { ScheduleLimits } from '@peach/types/tutors/consts.js';
import { tw } from '@tw';
import { useClock } from '@/lib/hooks/useClock';
import { useScheduleSelection } from '@/lib/hooks/useScheduleSelection';
import { useTimezone } from '@/lib/hooks/useTimezone';
import { useCalendarStore } from '@/lib/stores/calendar.store';
import { zoneName } from '@/lib/utils/when';
import { requestSchedule } from './schedule-request';
import type { CalendarFor } from '@/app/actions/bookings';

const EMPTY_SCHEDULE: CalendarGrid['available'] = {};

/** A tab left in the background this long comes back to a grid fetched again. */
const STALE_AFTER_MS = 5 * 60 * 1000;

/**
 * The server's notice window ROLLS, so a fetched grid goes on offering an hour
 * `getTimeslots` would refuse. `keep` is the visitor's own pick and survives:
 * swapping the hour under an open price card is worse than an honest no.
 */
function withoutExpired(
  available: CalendarGrid['available'],
  now: number,
  keep: number | null
): CalendarGrid['available'] {
  const cutoff = now + ScheduleLimits.minHoursBeforeClass * 60 * 60 * 1000;
  const kept: CalendarGrid['available'] = {};
  let expired = false;

  for (const [day, slots] of Object.entries(available)) {
    // Strictly after, because the server is: `isAfter(availableFrom)`.
    const live = slots.filter((slot) => {
      const start = new Date(slot.start).getTime();
      return start > cutoff || start === keep;
    });
    if (live.length !== slots.length) expired = true;
    if (live.length > 0) kept[day] = live;
  }

  return expired ? kept : available;
}

/** Only the slots starting inside [from, to): a subscription call is booked in its own week. */
function inside(
  available: CalendarGrid['available'],
  within: { from: string; to: string } | undefined
): CalendarGrid['available'] {
  if (!within) return available;
  const from = Date.parse(within.from);
  const to = Date.parse(within.to);
  const kept: CalendarGrid['available'] = {};
  for (const [day, slots] of Object.entries(available)) {
    const live = slots.filter((slot) => {
      const start = new Date(slot.start).getTime();
      return start >= from && start < to;
    });
    if (live.length > 0) kept[day] = live;
  }
  return kept;
}

interface SlotPickerProps {
  tutorUsername: string;
  calendar: CalendarFor;
  /** Reschedule: the call being moved must not block its own slot. */
  excludeClassId?: string;
  /** What stands in the picker's place when no time is open: a sentence, or a card. */
  empty: ReactNode;
  /** No label, no CTA: the tap on a time IS the pick, and whatever the caller
      renders below it carries the action. */
  label?: (selected: Dayjs | null) => string;
  busyLabel?: string;
  reloadToken?: number;
  /** The page clock, so a slot that crosses the notice window while the visitor
      is reading leaves the rail on its own. */
  serverNow: number;
  /** The hour the caller is already showing a price for. It survives the prune:
      swapping it under an open card is worse than an honest no at the tap. */
  keep?: string | null;
  /** A grid the caller already holds. Given one, the picker fetches nothing —
      the seam a demo of the booking step needs, so it can show a full calendar
      without a live schedule behind it and without a second picker existing. */
  grid?: CalendarGrid;
  /** The ground the picker is standing on, for the one line of its own prose:
      a coloured card speaks in white, a plain one in the card's ink. Nothing
      here inherits a colour — the page's own is the DARK theme's white, which
      is invisible on the white card the calls block is drawn in. */
  surface?: 'accent' | 'card';
  extra?: ReactNode;
  /** The caller's headline already names the zone, so the rail does not say it again. */
  zoneInTitle?: boolean;
  /** Offer only the slots starting in this range. */
  within?: { from: string; to: string };
  onPick: (isoTime: string, timezone: string) => Promise<string | void>;
  /** The visitor tapped a time themselves; a preselected one never calls it. */
  onChoose?: () => void;
}

export function SlotPicker({
  tutorUsername,
  calendar,
  excludeClassId,
  empty,
  label,
  busyLabel,
  reloadToken = 0,
  serverNow,
  keep = null,
  grid: givenGrid,
  surface = 'card',
  extra,
  zoneInTitle = false,
  within,
  onPick,
  onChoose,
}: SlotPickerProps) {
  const timezone = useTimezone();
  const now = useClock(serverNow);
  const [loadedGrid, setLoadedGrid] = useState<CalendarGrid | null>(null);
  const grid = givenGrid ?? loadedGrid;
  const [gridError, setGridError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dayRailScrolled, setDayRailScrolled] = useState(false);
  const [timeRailScrolled, setTimeRailScrolled] = useState(false);
  const [refetch, setRefetch] = useState(0);
  const calendarVersion = useCalendarStore((s) => s.versions[tutorUsername] ?? 0);
  // Primitives, so the fetch below re-runs when the subject changes, not on every render.
  const byCourse = 'courseId' in calendar;
  const calendarId = byCourse ? calendar.courseId : calendar.bundleId;

  // A refetch is silent: the grid on screen stays until the next one lands,
  // and the hook keeps a selection the new grid still offers.
  useEffect(() => {
    if (givenGrid || !timezone) return;
    void requestSchedule(
      tutorUsername,
      byCourse ? { courseId: calendarId } : { bundleId: calendarId },
      timezone,
      excludeClassId
    ).then((result) => {
      if (result.success) setLoadedGrid(result.data);
      else setGridError(true);
    });
  }, [
    givenGrid,
    timezone,
    tutorUsername,
    byCourse,
    calendarId,
    excludeClassId,
    reloadToken,
    refetch,
    calendarVersion,
  ]);

  useEffect(() => {
    let hiddenAt: number | null = null;
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else if (hiddenAt !== null && Date.now() - hiddenAt >= STALE_AFTER_MS) {
        setRefetch((n) => n + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const withinFrom = within?.from;
  const withinTo = within?.to;
  // Its own memo, so the clock's tick below keeps the windowed grid's identity.
  const offered = useMemo(
    () =>
      inside(
        grid?.available ?? EMPTY_SCHEDULE,
        withinFrom && withinTo ? { from: withinFrom, to: withinTo } : undefined
      ),
    [grid, withinFrom, withinTo]
  );
  const available = useMemo(
    () => withoutExpired(offered, now, keep ? Date.parse(keep) : null),
    [offered, now, keep]
  );

  const {
    selectedDate,
    selectedTime,
    availableDates,
    availableTimes,
    handleDateSelect,
    handleTimeSelect,
  } = useScheduleSelection({ schedule: available });

  const pick = useCallback(
    async (time: Dayjs) => {
      if (!timezone) return;
      setError(null);
      const failure = await onPick(time.toISOString(), timezone);
      if (failure) {
        setError(failure);
        // The server said no, so the rail is wrong somewhere: reload it.
        setRefetch((n) => n + 1);
      }
    },
    [onPick, timezone]
  );

  // Without a label the selection IS the pick: the card below asks for the
  // money, so it must follow the preselected time too, not only a tap. Not
  // `pick` — its synchronous setError is what the set-state-in-effect rule bans.
  useEffect(() => {
    if (label || !selectedTime || !timezone) return;
    void onPick(selectedTime.toISOString(), timezone).then((failure) => setError(failure ?? null));
  }, [label, selectedTime, timezone, onPick]);

  // Only the CTA has anything to say while it waits; a tap in the rail hands
  // over to the card below immediately.
  const submit = useCallback(async () => {
    if (!selectedTime) return;
    setBusy(true);
    await pick(selectedTime);
    setBusy(false);
  }, [selectedTime, pick]);

  if (!timezone || (!grid && !gridError)) {
    return <div className="mt-2.5 h-[120px] animate-pulse rounded-[10px] bg-[var(--sf-line)]/50" />;
  }

  if (gridError) {
    return (
      <span className="mt-2.5 block text-p-3 text-[var(--sf-mut)]">
        Could not load open times — try the refresh
      </span>
    );
  }

  if (availableDates.length === 0) {
    return typeof empty === 'string' ? (
      <span className="mt-2.5 block text-p-3 text-[var(--sf-mut)]">{empty}</span>
    ) : (
      <>{empty}</>
    );
  }

  return (
    <div className="mt-3 flex flex-col gap-2.5">
      <div
        onScroll={(e) => setDayRailScrolled(e.currentTarget.scrollLeft > 4)}
        className={tw(
          'sf-rail flex gap-2 overflow-x-auto pb-1',
          dayRailScrolled && 'sf-rail-scrolled'
        )}
      >
        {availableDates.map((day) => {
          const isSel = selectedDate ? day.isSame(selectedDate, 'day') : false;
          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => {
                setError(null);
                handleDateSelect(day);
              }}
              suppressHydrationWarning
              className={tw(
                'mm-btn flex min-w-[58px] flex-shrink-0 flex-col items-center gap-0.5 rounded-xl border-[1.5px] px-2.5 py-2',
                isSel
                  ? 'border-[var(--sf-ink)] bg-[var(--sf-ink)] text-[var(--sf-page)]'
                  : 'border-[var(--sf-line)] bg-[var(--sf-card)] text-[var(--sf-ink)]'
              )}
            >
              <span className="sf-eyebrow text-[10px] font-bold opacity-60">
                {day.format('ddd')}
              </span>
              <span className="sf-num text-head-2 font-extrabold leading-none">
                {day.format('D')}
              </span>
              <span className="sf-eyebrow text-[9px] font-bold opacity-50">
                {day.format('MMM')}
              </span>
            </button>
          );
        })}
      </div>

      {selectedDate && (
        <div
          onScroll={(e) => setTimeRailScrolled(e.currentTarget.scrollLeft > 4)}
          className={tw(
            'sf-rail flex gap-1.5 overflow-x-auto pb-1',
            timeRailScrolled && 'sf-rail-scrolled'
          )}
        >
          {availableTimes.map((time) => {
            const isSel = selectedTime?.isSame(time);
            return (
              <button
                key={time.toISOString()}
                type="button"
                onClick={() => {
                  setError(null);
                  handleTimeSelect(time);
                  onChoose?.();
                }}
                suppressHydrationWarning
                className={tw(
                  'mm-btn sf-num min-w-[68px] flex-shrink-0 rounded-lg border-[1.5px] border-[var(--sf-line)] bg-[var(--sf-card)] px-3 py-2 text-center text-badge font-bold text-[var(--sf-ink)]',
                  isSel && 'border-[var(--sf-ink)] bg-[var(--sf-ink)] text-[var(--sf-page)]'
                )}
              >
                {time.format('HH:mm')}
              </button>
            );
          })}
        </div>
      )}

      {/* Whose clock these are, said once on the screen and under the times
          themselves. Every card above the rail offers a DAY, which needs no
          zone; the hours do, because they are converted to the reader's. */}
      {selectedDate && !zoneInTitle && (
        <span
          className={tw(
            'text-badge',
            surface === 'accent' ? 'text-white/70' : 'text-[var(--sf-mut)]'
          )}
        >
          Times in {zoneName(timezone)}
        </span>
      )}

      {selectedTime && extra}

      {/* Cleared by every rail tap: a verdict left under the next pick reads as
          the state of the calendar, and one refusal became forty dead taps. */}
      {error && <span className="text-p-3 font-semibold text-error">{error}</span>}

      {label && (
        <button
          type="button"
          disabled={!selectedTime || busy}
          onClick={() => void submit()}
          className="mm-btn sf-cta sf-cta-ink"
          suppressHydrationWarning
        >
          {busy ? busyLabel : label(selectedTime)}
        </button>
      )}
    </div>
  );
}
